/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import type { AxiosError } from 'axios'
import toast from 'react-hot-toast'
import { appsApi } from '../api'
import { useAuth } from '../lib/auth'
import type { AppWithStats, AppCreate, AppUpdate } from '../types'

interface AppsContextType {
  apps: AppWithStats[]
  isLoading: boolean
  error: string | null
  createApp: (data: AppCreate) => Promise<void>
  updateApp: (id: string, data: AppUpdate) => Promise<void>
  deleteApp: (id: string) => Promise<void>
  getApp: (id: string) => AppWithStats | undefined
  refreshApps: () => Promise<void>
}

const AppsContext = createContext<AppsContextType | undefined>(undefined)

export function AppsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth()
  const [apps, setApps] = useState<AppWithStats[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refreshApps = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setApps([])
      setIsLoading(false)
      return
    }

    try {
      setError(null)
      setIsLoading(true)
      const response = await appsApi.list()
      setApps(response.apps)
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>
      const errorMessage = axiosError.response?.data?.detail || 'Failed to load apps'
      setError(errorMessage)
      console.error('Failed to load apps:', err)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, user])

  // Fetch apps when authentication state changes
  useEffect(() => {
    // Wait for auth to finish loading and user to be available
    if (authLoading || !user) return
    refreshApps()
  }, [isAuthenticated, authLoading, user, refreshApps])

  const createApp = async (data: AppCreate) => {
    try {
      await appsApi.create(data)
      toast.success('App created successfully')
      await refreshApps()
    } catch (err) {
      // Client interceptor transforms errors to plain Error with message extracted
      const errorMessage = err instanceof Error ? err.message : 'Failed to create app'
      toast.error(errorMessage)
      throw err
    }
  }

  const updateApp = async (id: string, data: AppUpdate) => {
    try {
      await appsApi.update(id, data)
      toast.success('App updated successfully')
      await refreshApps()
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update app'
      toast.error(errorMessage)
      throw err
    }
  }

  const deleteApp = async (id: string) => {
    try {
      await appsApi.delete(id)
      setApps(apps.filter(a => a.id !== id))
      toast.success('App deleted')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete app'
      toast.error(errorMessage)
      throw err
    }
  }

  const getApp = (id: string) => {
    return apps.find(a => a.id === id)
  }

  return (
    <AppsContext.Provider
      value={{
        apps,
        isLoading,
        error,
        createApp,
        updateApp,
        deleteApp,
        getApp,
        refreshApps,
      }}
    >
      {children}
    </AppsContext.Provider>
  )
}

export function useApps() {
  const context = useContext(AppsContext)
  if (context === undefined) {
    throw new Error('useApps must be used within an AppsProvider')
  }
  return context
}
